// eslint-disable-next-line
///<reference path="../../../../../../../jsdoc-types.js" />

import pMinDelay from 'p-min-delay';
import { parseRelativeUrl, urlWithoutParams } from '../../../../common/utils/url';
import loadColorVariation from '../../../utils/loadColorVariation';
import { getAppliedSortingOption, hasAnySortingOptionApplied, isRefinementValueForPrice } from '../../../utils/productSearch';
import { comparePageNumber, haveSameCategory } from './compareProductSearch';
import createCategorySeparator from './createCategorySeparator';
import { createProductSearchPrefetcher } from '../../../utils/createProductSearchPrefetcher';
import getFrontConfig from '../../../../common/getFrontConfig';

const FRONT_CONFIG = getFrontConfig();

export const types = {
  applyFilters: 'productList/applyFilters',
  hide: 'productList/hide',
  init: 'productList/init',
  loadColorVariation: 'productList/loadColorVariation',
  loadFilteredListFromUrl: 'productList/loadFilteredListFromUrl',
  loadNewListFromUrl: 'productList/loadNewListFromUrl',
  loadPageFromUrl: 'productList/loadPageFromUrl',
  resetList: 'productList/resetList',
  resetUiFilters: 'productList/resetUiFilters',
  setVisibleCategory: 'productList/setVisibleCategory',
  selectFilter: 'productList/selectFilter',
  deselectFilter: 'productList/deselectFilter',
  deselectPriceFilter: 'productList/deselectPriceFilter',
  selectSortingOption: 'productList/selectSortingOption',
  setColumnMode: 'productList/setColumnMode',
  show: 'productList/show',
};

export const getters = {
  products: 'productList/products',
  numAppliedFilters: 'productList/numAppliedFilters',
  uiNumAppliedFilters: 'productList/uiNumAppliedFilters',
  uiNumSelectedFilters: 'productList/uiNumSelectedFilters',
  visibleCategory: 'productList/visibleCategory',
};

let productSearchPrefetcher;

const mutateCategory = ({ commit, state }, productSearch) => {
  let mutation = null;

  if (haveSameCategory(state.productSearch, productSearch)) {
    const pageNumberComparison = comparePageNumber(state.productSearch, productSearch);

    if (pageNumberComparison === 1) {
      mutation = 'appendCategoryPage';
    } else if (pageNumberComparison === -1) {
      mutation = 'prependCategoryPage';
    }
  } else {
    mutation = 'appendAnotherCategoryPage';
  }

  if (mutation) {
    commit(mutation, productSearch);
  }

  return productSearch;
};

const stateEmptyList = {
  filtersUrl: null,
  loadedUrls: [],
  products: [],
  productSearch: null,
  visibleCategory: null,
};

const filtersWithoutCategory = (filters) => filters?.filter(
  (filter) => filter.type !== 'category',
);

/**
 * Remove any applied price filter from a state
 * @param {ProductListState} state - state of the module
 * @returns {undefined}
 */
function removeAnyPriceFilterFromState(state) {
  state.ui.selectedFilters = state.ui.selectedFilters.filter(
    (filter) => !isRefinementValueForPrice(filter),
  );
}

/**
 * @typedef ProductListState
 * @type {Object}
 * @property {Object} ui ui related flags
 * @property {string} [ui.columnMode="default"] Column mode used in grid (normal or compressed columns)
 * @property {boolean} [ui.hidden=true] Is PLP hidden
 * @property {boolean} ui.moreCategoriesAppended PLP have shown products from any following category
 * @property {boolean} ui.moreProductsAppended PLP have shown products from any following page
 * @property {ProductSearchRefinement[]} ui.initialRefinements - original refinements from not filtered productSearch
 * @property {SortingOption[]} ui.initialSortingOptions original sortingOptions from not sorted prodcutSearch
 * @property {ProductSearchRefinementValue[]} ui.appliedFilters applied refinement values in ui
 * @property {SortingOption} ui.appliedSortingOption applied sortingOption in ui
 * @property {ProductSearchRefinementValue[]} ui.selectedFilters selected refinement values in ui
 * @property {SortingOption} ui.selectedSortingOption selected sortingOption in ui
 * @property {boolean} loading Is there any pending request
 * @property {Object} caches
 * @property {Object.<string, Object>} caches.categoriesByUrl loaded category objects by url
 * @property {string} filtersUrl last filters URL used to filter items in PLP. Useful for not refetch
 * @property {string[]} [loadedUrls=[]] URLs used to add items to PLP. Useful for not refetch
 * current data when going back in history
 * @property {{category:object,items:object[]}[]} products - Tile model arrays grouped by category
 * @property {ProductSearch} productSearch - Last search related data (count, pageNumber, pageSize,
 * products of the page, selected filters...)
 * @property {object} visibleCategory - Current category data (id, name, pageDescription, pageKeywords,
 * pageTitle...)
 */

/** @type ProductListState  */
const stateInitial = {
  ui: {
    columnMode: FRONT_CONFIG.vue.TheProductList.columnMode.default,
    hidden: true,
    moreProductsAppended: false,
    moreProductsPrepended: false,
    moreCategoriesAppended: false,
    initialRefinements: [],
    initialSortingOptions: [],
    appliedFilters: [],
    appliedSortingOption: null,
    selectedFilters: [],
    selectedSortingOption: null,
  },
  caches: {
    categoriesByUrl: {},
  },
  loading: false,
  ...stateEmptyList,
};

export default {
  namespaced: true,
  state: stateInitial,
  mutations: {
    show(state) {
      state.ui.hidden = false;
    },
    hide(state) {
      state.ui.hidden = true;
    },
    filtered(state, url) {
      state.filtersUrl = url;
    },
    loaded(state, url) {
      state.loading = false;
      state.loadedUrls = [...state.loadedUrls, url];
      state.filtersUrl = state.filtersUrl || url;
    },
    loading(state, resetList = false) {
      state.loading = true;
      if (resetList) {
        Object.assign(state, stateEmptyList);
      }
    },
    appendAnotherCategoryPage(state, productSearch) {
      state.ui.moreCategoriesAppended = true;
      state.ui.moreProductsAppended = true;
      state.products = [
        ...state.products,
        {
          category: productSearch.category,
          items: productSearch.products,
        },
      ];
      state.productSearch = productSearch;
      state.caches.categoriesByUrl[productSearch.category.url] = productSearch.category;
    },
    appendCategoryPage(state, productSearch) {
      state.ui.moreProductsAppended = true;
      const currentCategory = state.products[state.products.length - 1];
      currentCategory.items = [
        ...currentCategory.items,
        ...productSearch.products,
      ];
      state.productSearch = productSearch;
    },
    prependCategoryPage(state, productSearch) {
      state.ui.moreProductsPrepended = true;
      const currentCategory = state.products[0];
      currentCategory.items = [
        ...productSearch.products,
        ...currentCategory.items,
      ];
      state.productSearch = productSearch;
    },
    /**
     * Set selected filters as applied filters in ui
     * @param {ProductListState} state - state of the module
     */
    applyFilters(state) {
      state.ui.appliedFilters = state.ui.selectedFilters;
      state.ui.appliedSortingOption = state.ui.selectedSortingOption;
    },
    /**
     * Set sorting and filtering to default value
     * @param {ProductListState} state - state of the module
     * @param {ProductSearch} [productSearch=null] - product search used as initial state of the filters
     */
    resetFilters(state, productSearch = null) {
      state.ui.initialRefinements = [];
      state.ui.initialSortingOptions = null;
      state.ui.appliedFilters = [];
      state.ui.appliedSortingOption = null;
      state.ui.selectedFilters = [];
      state.ui.selectedSortingOption = null;

      if (productSearch) {
        state.ui.initialRefinements = productSearch.refinements;
        state.ui.initialSortingOptions = productSearch.productSort.options;
        const sortingOption = getAppliedSortingOption(productSearch);
        if (sortingOption !== null) {
          state.ui.appliedSortingOption = sortingOption;
          state.ui.selectedSortingOption = sortingOption;
        }
        state.ui.appliedFilters = filtersWithoutCategory(productSearch.selectedFilters);
        state.ui.selectedFilters = filtersWithoutCategory(productSearch.selectedFilters);
      }
    },
    /**
     * Reset selected filters/sorting options in ui
     * @param {ProductListState} state - state of the module
     */
    resetUiFilters(state) {
      state.ui.appliedFilters = [];
      state.ui.appliedSortingOption = null;
      state.ui.selectedFilters = [];
      state.ui.selectedSortingOption = null;
    },
    resetStateList: (state, productSearch) => {
      state.ui.moreCategoriesAppended = false;
      state.ui.moreProductsAppended = false;
      state.visibleCategory = productSearch.category;
      state.products = [
        {
          category: productSearch.category,
          items: productSearch.products,
        },
      ];
      state.productSearch = productSearch;
      state.caches.categoriesByUrl[productSearch.category.url] = productSearch.category;
    },
    /**
     * Add a filter to filters
     * @param {ProductListState} state - state of the module
     * @param {ProductSearchRefinementValue} refinementValue - selected filter
     */
    selectFilter(state, refinementValue) {
      if (isRefinementValueForPrice(refinementValue)) {
        // Remove any previous price filter to avoid adding more than one range
        removeAnyPriceFilterFromState(state);
      }
      state.ui.selectedFilters.push(refinementValue);
    },
    /**
     * Remove a filter from filters
     * @param {ProductListState} state - state of the module
     * @param {ProductSearchRefinementValue} refinementValue - selected filter
     */
    deselectFilter(state, refinementValue) {
      state.ui.selectedFilters = state.ui.selectedFilters.filter(
        (selectedFilter) => (selectedFilter.value !== refinementValue.value),
      );
    },
    /**
     * Remove any applied price filter
     * @param {ProductListState} state - state of the module
     */
    deselectPriceFilter: removeAnyPriceFilterFromState,
    /**
     * Set sort to the given option
     * @param {ProductListState} state - state of the module
     * @param {SortingOption} sortingOption - selected sorting option
     */
    selectSortingOption(state, sortingOption) {
      state.ui.selectedSortingOption = sortingOption;
    },
    /**
     * Set PLP column mode (num cols)
     * @param {ProductListState} state - state of the module
     * @param {string} columnMode - column mode to apply
     */
    setColumnMode(state, columnMode) {
      state.ui.columnMode = columnMode;
    },
    setVisibleCategory(state, category) {
      state.visibleCategory = category;
    },
  },
  actions: {
    init({ commit, dispatch }, { productSearch, url }) {
      productSearchPrefetcher = createProductSearchPrefetcher(
        productSearch,
      );
      dispatch('resetList', productSearch);
      commit('resetFilters', productSearch);
      commit('loaded', parseRelativeUrl(url));
      commit('loaded', parseRelativeUrl(productSearch.permalink));
    },
    loadPageFromUrl({ commit, state }, { url, minLoadingTime = 0, prefetchAdjacent = true }) {
      if (state.loadedUrls.indexOf(url) > -1) {
        return Promise.resolve(url);
      }
      commit('loading');

      const pagePromise = Number.isInteger(minLoadingTime) && minLoadingTime > 0
        ? pMinDelay(productSearchPrefetcher.get(url), minLoadingTime)
        : productSearchPrefetcher.get(url, prefetchAdjacent);

      return pagePromise.then((productSearch) => {
        mutateCategory({ commit, state }, productSearch);
        commit('loaded', url);
        return url;
      });
    },
    loadColorVariation(productListModule, {
      color, type, index, expand,
    }) {
      loadColorVariation(
        productListModule,
        {
          color,
          type,
          index,
          expand,
        },
      );
    },
    /**
     * Reset the list and load a new one filtered with data from a new URL
     *
     * @param {object} context
     * @param {string} url - URL to load a new product search
     * @returns
     */
    loadFilteredListFromUrl({ commit, dispatch, state }, url) {
      if (state.filtersUrl === url) {
        return Promise.resolve(url);
      }
      commit('loading', true);
      productSearchPrefetcher = createProductSearchPrefetcher();
      return productSearchPrefetcher.get(url).then((productSearch) => {
        dispatch('resetList', productSearch);
        commit('loaded', urlWithoutParams(url));
        commit('loaded', url);
        commit('filtered', url);
        return url;
      });
    },
    /**
     * Reset the list and load a new one with data from a new URL
     *
     * @param {object} context
     * @param {ProductListState} context.state - current state
     * @param {string} url - URL to load a new product search
     * @returns
     */
    loadNewListFromUrl({ commit, dispatch, state }, url) {
      if (state.caches.categoriesByUrl[url]) {
        /* Set visible category if it is cached. Useful temporary patch for
        refreshing breadcrumbs when navigating between categories from menu
        without reloading. Only suitable for categories loaded in a long PLP. */
        /* TODO: Remove this patch after improving the observers that detect
        the visible category */
        commit('setVisibleCategory', state.caches.categoriesByUrl[url]);
        dispatch('categories/setCategory', state.caches.categoriesByUrl[url], { root: true });
      }
      if (state.loadedUrls.indexOf(url) > -1) {
        return Promise.resolve(url);
      }
      commit('loading', true);
      // Reset list before loading new category initial page
      productSearchPrefetcher = createProductSearchPrefetcher();
      commit('resetFilters');
      return productSearchPrefetcher.get(url).then((productSearch) => {
        dispatch('resetList', productSearch);
        commit('resetFilters', productSearch);
        commit('loaded', urlWithoutParams(url));
        commit('loaded', url);
        return url;
      });
    },
    resetList: ({ commit, dispatch }, productSearch) => {
      commit('resetStateList', productSearch);
      dispatch('categories/setCategory', productSearch.category, { root: true });
    },
  },
  getters: {
    /**
     * Retrieve the number of applied filters and sorting in current productSearch
     * Skip category filters
     * @param {ProductListState} state
     * @returns {number}
     */
    numAppliedFilters: (state) => {
      const applicableFilters = filtersWithoutCategory(state.productSearch?.selectedFilters);
      const numFilters = applicableFilters?.length;
      const hasSorting = hasAnySortingOptionApplied(state.productSearch);
      let result = numFilters;
      if (hasSorting) {
        result = numFilters + 1;
      }
      return result;
    },
    /**
     * Retrieve the number of ui applied filters and sorting in current productSearch
     * Skip category filters
     * @param {ProductListState} state
     * @returns {number}
     */
    uiNumAppliedFilters: (state) => {
      let count = state.ui.appliedFilters.length;
      if (state.ui.appliedSortingOption !== null) {
        count += 1;
      }
      return count;
    },
    /**
     * Retrieve the number of ui selected filters and sorting in current productSearch
     * even if they are not applied yet
     * Skip category filters
     * @param {ProductListState} state
     * @returns {number}
     */
    uiNumSelectedFilters: (state) => {
      let count = state.ui.selectedFilters.length;
      if (state.ui.selectedSortingOption !== null) {
        count += 1;
      }
      return count;
    },
    /**
     * Retrieve the product list
     * @returns {[object]}
     */
    products: (state) =>
      state.products.flatMap((categoryResults, index) =>
        (index > 0
          ? [
            createCategorySeparator(
              categoryResults.category, // current category
              state.products[index - 1].category, // previous category
            ),
            ...categoryResults.items,
          ] // Add linebreaks between categories
          : categoryResults.items)),
    visibleCategory: (state) => (!state.ui.hidden ? state.visibleCategory : null),
  },
};
